import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, FileSearch, Send, Briefcase, ChevronRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function HowItWorksPage() {
  const steps = [{
    id: '01',
    title: 'Free Consultation',
    icon: MessageCircle,
    desc: 'We start with a deep dive into your career history, goals, and aspirations. This 30-minute session helps us understand exactly what you are looking for.',
    details: ['Career assessment', 'Goal setting', 'Gap analysis']
  }, {
    id: '02',
    title: 'Personalized Strategy',
    icon: FileSearch,
    desc: 'Our team crafts a tailored plan including resume optimization, LinkedIn overhaul, and a targeted list of potential employers.',
    details: ['Document creation', 'Profile optimization', 'Target list building']
  }, {
    id: '03',
    title: 'Application & Support',
    icon: Send,
    desc: 'We help you apply to roles, leveraging our network for referrals. We also provide cover letter customization and interview prep.',
    details: ['Direct applications', 'Network referrals', 'Interview coaching']
  }, {
    id: '04',
    title: 'Placement & Follow-up',
    icon: Briefcase,
    desc: 'Once you land the job, we help with salary negotiation and onboarding advice to ensure a smooth transition.',
    details: ['Offer negotiation', 'Onboarding tips', '30-day check-in']
  }];
  return <div className="pt-20" data-id="element-410" data-mp="1j6lbmfalbzst">
      {/* Hero */}
      <section className="bg-navy-900 text-white py-20 text-center" data-id="element-411" data-mp="1j4s2mtci93hn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-412" data-mp="1nxbenlvjnqkc">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-413" data-mp="1nyw7n727n1g3">
            How It Works
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-414" data-mp="vgin295vxbyy">
            Our proven 4-step process designed to take you from application to
            offer letter.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 relative overflow-hidden" data-id="element-415" data-mp="1trjqfxzvr29v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-416" data-mp="28h7v9979sj2c">
          <div className="space-y-24 relative" data-id="element-417" data-mp="28goa7jmbpek3">
            {/* Vertical Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -z-10 hidden md:block" data-id="element-418" data-mp="23iwlszkw0akn" />

            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`flex flex-col md:flex-row items-center gap-12 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-419" data-mp="1atz2qu8uq9qu" data-mpi={index}>
                <div className="flex-1 text-center md:text-left" data-id="element-420" data-mp="2w94y4asqs0rb">
                  <div className={`flex items-center gap-4 mb-4 justify-center ${index % 2 === 1 ? 'md:justify-end' : 'md:justify-start'}`} data-id="element-421" data-mp="33przk3ag3hxm">
                    <span className="text-6xl font-bold text-gray-100" data-id="element-422" data-mp="2tt6ob07u1alc">
                      {step.id}
                    </span>
                    <div className="p-3 bg-gold-100 rounded-full text-gold-600" data-id="element-423" data-mp="2yrqfpg573el3">
                      <step.icon className="h-8 w-8" data-id="element-424" data-mp="3midnm1f4247n" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-425" data-mp="3reufn6pfl2q0">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-lg" data-id="element-426" data-mp="3cp6jsl00qw9o">{step.desc}</p>
                  <ul className={`space-y-2 inline-block text-left`} data-id="element-427" data-mp="3hornt9u1put9">
                    {step.details.map((detail, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-428" data-mp="98ps2wg4d1xu" data-mpi={i}>
                        <ChevronRight className="h-4 w-4 text-gold-500 mr-2" data-id="element-429" data-mp="e2k9tuc5z6ua" />
                        {detail}
                      </li>)}
                  </ul>
                </div>
                <div className="flex-1 flex justify-center" data-id="element-430" data-mp="2hhxo9d1v1bip">
                  <div className="w-12 h-12 bg-navy-900 rounded-full border-4 border-white shadow-lg z-10 hidden md:flex items-center justify-center" data-id="element-431" data-mp="2cl75uj2l23u4">
                    <div className="w-3 h-3 bg-gold-500 rounded-full" data-id="element-432" data-mp="27nfh1xx8ixu5" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-433" data-mp="30a05oqmbsvu0" /> {/* Spacer for grid alignment */}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-20" data-id="element-434" data-mp="2vgni8jtduv2u">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-435" data-mp="2qkoyxamww39a">
          <div className="text-center mb-16" data-id="element-436" data-mp="3nzb07qd31tzp">
            <h2 className="text-3xl font-serif font-bold text-navy-900 mb-4" data-id="element-437" data-mp="3o02w72aldtqi">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6" data-id="element-438" data-mp="3rh8h96bbytcv">
            {[{
            q: 'How long does the process take?',
            a: 'On average, our clients secure interviews within 2 weeks and offers within 6 weeks.'
          }, {
            q: 'Do you work with all industries?',
            a: 'Yes, we have specialists across Tech, Healthcare, Finance, Marketing, and Engineering.'
          }, {
            q: 'Is there a guarantee?',
            a: "We offer a 60-day interview guarantee. If you don't get an interview, we rewrite your resume for free."
          }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-439" data-mp="n53gvyjp5lso" data-mpi={i}>
                <h4 className="text-lg font-bold text-navy-900 mb-2 flex items-center" data-id="element-440" data-mp="s1ll8xb47i4h">
                  <HelpCircle className="h-5 w-5 text-gold-500 mr-3" data-id="element-441" data-mp="x2hmf3430q67" />
                  {faq.q}
                </h4>
                <p className="text-gray-600 ml-8" data-id="element-442" data-mp="120j5ho1icpho">{faq.a}</p>
              </div>)}
          </div>

          <div className="text-center mt-12" data-id="element-443" data-mp="ca1ack133au0">
            <Button variant="primary" href="/contact" data-id="element-444" data-mp="7dkknle8koet">
              Still have questions?
            </Button>
          </div>
        </div>
      </section>
    </div>;
}